import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getAllApprovedModels,
  createApprovedModel,
  updateApprovedModel,
  deactivateApprovedModel,
  deprecateApprovedModel,
  deleteApprovedModel,
  getModelUsage,
  detectModelCapabilities
} from '../services/api/approvedBedrockModelApi';

/**
 * Custom hook for managing approved Bedrock models
 * 
 * Handles all CRUD operations, lifecycle management (deactivate, deprecate, delete),
 * and usage checking for approved models.
 * 
 * @returns {object} Approved models state and operations
 */
export const useApprovedModels = () => {
  // State
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);

  /**
   * Fetch all approved models
   */
  const fetchModels = useCallback(async () => {
    if (permissionDenied) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getAllApprovedModels();
      setModels(response.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setPermissionDenied(true);
        setError('You do not have permission to view approved models');
      } else {
        const errorMessage = err.response?.data?.message || 'Failed to load approved models';
        setError(errorMessage);
      }
      console.error('Error fetching approved models:', err);
    } finally {
      setLoading(false);
    }
  }, [permissionDenied]);

  /**
   * Create a new approved model
   * @param {object} data - Model data to create
   * @returns {Promise<object>} Created model
   */
  const createModel = async (data) => {
    try {
      setError(null);
      setSuccessMessage('');
      const response = await createApprovedModel(data);
      const newModel = response.data;
      
      // Add to local state
      setModels(prev => [...prev, newModel]);
      setSuccessMessage('Model created successfully');
      
      return newModel;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create model';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Update an existing approved model
   * @param {string} id - Model ID
   * @param {object} data - Updated model data
   * @returns {Promise<object>} Updated model
   */
  const updateModel = async (id, data) => {
    try {
      setError(null);
      setSuccessMessage('');
      const response = await updateApprovedModel(id, data);
      const updatedModel = response.data;
      
      // Update in local state
      setModels(prev => prev.map(m => m.id === id ? updatedModel : m));
      setSuccessMessage('Model updated successfully');
      
      return updatedModel;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update model';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Deactivate a model (validates not in use first)
   * @param {string} id - Model ID
   * @returns {Promise<void>}
   */
  const deactivateModel = async (id) => {
    try {
      setError(null);
      setSuccessMessage('');
      await deactivateApprovedModel(id);
      
      // Update in local state
      setModels(prev => prev.map(m => 
        m.id === id ? { ...m, isActive: false } : m
      ));
      setSuccessMessage('Model deactivated successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to deactivate model';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Deprecate a model (allows existing use, prevents new use)
   * @param {string} id - Model ID
   * @returns {Promise<void>}
   */
  const deprecateModel = async (id) => {
    try {
      setError(null);
      setSuccessMessage('');
      await deprecateApprovedModel(id);
      
      // Update in local state
      setModels(prev => prev.map(m => 
        m.id === id ? { ...m, deprecatedAt: new Date().toISOString() } : m
      ));
      setSuccessMessage('Model deprecated successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to deprecate model';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Delete a model (validates not in use first)
   * @param {string} id - Model ID
   * @returns {Promise<void>}
   */
  const deleteModel = async (id) => {
    try {
      setError(null);
      setSuccessMessage('');
      await deleteApprovedModel(id);
      
      // Remove from local state
      setModels(prev => prev.filter(m => m.id !== id));
      setSuccessMessage('Model deleted successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete model';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Check if a model is currently in use by any customer configuration
   * @param {string} id - Model ID
   * @returns {Promise<object>} Usage information
   */
  const checkUsage = async (id) => {
    try {
      setError(null);
      const response = await getModelUsage(id);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to check model usage';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Detect document support for a model by probing the Bedrock Converse API
   * @param {string} id - Model ID
   * @returns {Promise<object>} Updated model with detected capabilities
   */
  const detectCapabilities = async (id) => {
    try {
      setError(null);
      setSuccessMessage('');
      const response = await detectModelCapabilities(id);
      const updatedModel = response.data;

      // Update in local state
      setModels(prev => prev.map(m => m.id === id ? updatedModel : m));
      setSuccessMessage(`Document support detected: ${updatedModel.supportsDocuments ? 'Yes' : 'No'}`);

      return updatedModel;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to detect model capabilities';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear success message
   */
  const clearSuccessMessage = useCallback(() => {
    setSuccessMessage('');
  }, []);

  /**
   * Auto-fetch on mount
   */
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchModels();
    }
  }, [fetchModels]);

  return {
    models,
    loading,
    error,
    successMessage,
    permissionDenied,
    fetchModels,
    createModel,
    updateModel,
    deactivateModel,
    deprecateModel,
    deleteModel,
    checkUsage,
    detectCapabilities,
    clearError,
    clearSuccessMessage
  };
};
